// src/com/veh_info/VehInfoMyDcRented.js
import React, { useEffect, useRef, useState } from 'react';
//import { styled } from '@mui/material/styles';
import { IconButton, CircularProgress, Button } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import {
    ContentConainer, ContentHeader, ControlBox, CountLabel, ToolBox
} from '../../common/TopViewControl';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import { authTableTreeRepo, vehInfoListMyDcRentedRepo } from '../../value/AppModel';
import {
    ADD_VEH_INFO_RENTAL, EDIT_VEH_INFO_RENTAL, GET_VEH_INFO,
    LIST_VEH_INFO_OF_SESSION_DC_RENT, REMOVE_VEH_INFO_RENTAL
} from './VehInfoGql';
import { VehInfoDictionary } from './VehInfoDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import VehInfoInputRental from './VehInfoInputRental';

/**
 * 용차관리
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-10-08 09:43:36 using DbdbTap
 */
export default function VehInfoMyDcRented() {
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const [countToDisplay, setCountToDisplay] = useState(0);
    const vehInfoListData = useReactiveVar(vehInfoListMyDcRentedRepo);
    const [editorState, setEditorState] = useState(AppObject.EmptyViewerState);
    const [searchText, setSearchText] = useState('');
    const addAlert = useAlertSetter();
    const openConfirmDialog = useConfirmDialog();
    const checkAuth = useAuthorizationCheck();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getVehInfoList, responseList] = useLazyQuery(LIST_VEH_INFO_OF_SESSION_DC_RENT, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: VehInfoDictionary.errorTitle.Get, message: [error.message]})
    });
    const [getVehInfoItem, responseItem] = useLazyQuery(GET_VEH_INFO, {
		...AppObject.NoCachedFetch,
		onCompleted: (data, option) => {onCompleteGetItem(data, option)},
		onError: (error) => addAlert({title: VehInfoDictionary.errorTitle.Get, message: [error.message]})
	});
    const [addVehInfoRental, responseAdd] = useMutation( ADD_VEH_INFO_RENTAL, {
		onCompleted: (data, option) => onCompleteAddRental(data, option), 
		onError: (error) => addAlert({title: VehInfoDictionary.errorTitle.Add, message: [error.message]})
	} );
    const [editVehInfoRental, responseEdit] = useMutation( EDIT_VEH_INFO_RENTAL, {
		onCompleted: (data, option) => onCompleteEditRental(data, option),
		onError: (error) => addAlert({title: VehInfoDictionary.errorTitle.Edit, message: [error.message]})
	} );
    const [removeVehInfoRental, responseRemove] = useMutation( REMOVE_VEH_INFO_RENTAL, {
		onCompleted: (data, option) => onCompleteRemoveRental(data, option),
		onError: (error) => addAlert({title: VehInfoDictionary.errorTitle.Remove, message: [error.message]})
	});

    const getVehInfoListNow = () => {
        getVehInfoList();
        lastReloadRef.current = new Date().getTime()/1000;
    };

    useEffect(() => {
        const diff = new Date().getTime()/1000 - vehInfoListData.when;
        if(diff > 300)
            getVehInfoListNow();
    }, []);


    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.vehInfoListOfSessionDcRent) {
            vehInfoListMyDcRentedRepo({when:new Date().getTime()/1000, records:data.vehInfoListOfSessionDcRent});
        }
    };

    const onCompleteGetItem = (data, option) => {
        if(data.vehInfoItem) {
            setEditorState({open:true, item:data.vehInfoItem});
        }
        else addAlert({title:VehInfoDictionary.errorTitle.Get, message:['지정한 차량을 찾을 수 없습니다.']});
    };

    const onCompleteAddRental = (data, clientOption) => {
        if(data.vehInfoAddRental.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getVehInfoListNow();
        }
        else addAlert({title:VehInfoDictionary.errorTitle.Add, message:[data.vehInfoAddRental.message]});
    };

    const onCompleteEditRental = (data, clientOption) => {
        if(data.vehInfoEditRental.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getVehInfoListNow();
        }
        else addAlert({title:VehInfoDictionary.errorTitle.Edit, message:[data.vehInfoEditRental.message]});
    };

    const onCompleteRemoveRental = (data, clientOption) => {
        if(data.vehInfoRemoveRental.ok) getVehInfoListNow();
        else addAlert({title:VehInfoDictionary.errorTitle.Remove, message:[data.vehInfoRemoveRental.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 60) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotInOneMinute, {variant:'info', autoHideDuration:2000});
        }
        else getVehInfoListNow();
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onRequestSave = (item, isEdit) => {
        const mutateParam = {variables:{vehInfo:item}};
        if(isEdit) editVehInfoRental(mutateParam);
        else addVehInfoRental(mutateParam);
    };

    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "용차 정보 삭제",
            content: [
                `${item.vehPlates} 차량을 삭제하시겠습니까?`,
                '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });

        if(confirm)
            removeVehInfoRental({variables:{vehInfo:{vehInfoSeq:item.vehInfoSeq}}});
    };

    const onClickAddRental = () => {
        setEditorState({open:true, item:null});
    };

    const onRequestEdit = (item) => {
        getVehInfoItem({variables:{vehInfoSeq:item.vehInfoSeq}});
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.DELETE: confirmRemove(row); break;
            case AppObject.command.EDIT: onRequestEdit(row); break;
            default: return;
        }
    };

    /*
	CALL wps_set_auth_table('pr_veh_info_add_rental', '7', v_msg); -- 새로운 용차를 등록
	CALL wps_set_auth_table('pr_veh_info_edit_rental', '7', v_msg); -- 용차 기록 변경
	CALL wps_set_auth_table('pr_veh_info_remove_rental', '7', v_msg); -- 용차 기록 삭제
    */
    const canAdd = checkAuth(authTableTree.tree.pr_veh_info_add_rental);
    const canEdit = checkAuth(authTableTree.tree.pr_veh_info_edit_rental);
    const canRemove = checkAuth(authTableTree.tree.pr_veh_info_remove_rental);

    const tools = [];
    if(canEdit) tools.push({id:AppObject.command.EDIT, icon:<EditIcon/>});
    if(canRemove) tools.push({id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning});

    const onDoubleClickRow = (record) => {
        if(canEdit) getVehInfoItem({variables:{vehInfoSeq:record.vehInfoSeq}});
    };

    const loadingAny
    = responseList.loading
    || responseItem.loading
    || responseAdd.loading
    || responseEdit.loading
    || responseRemove.loading;

    return(
        <ContentConainer>
            <ContentHeader>
                <ControlBox>
                    <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                    />
                </ControlBox>
                <ToolBox>
                    <CountLabel count={countToDisplay} />
                    {
                        loadingAny ?
                        <CircularProgress size={20} />
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                    {
                        canAdd &&
                        <Button
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                            startIcon={<AddIcon />}
                            style={{marginLeft: 5}}
                            onClick={onClickAddRental}
                        >용차 추가</Button>
                    }
                </ToolBox>
            </ContentHeader>
            <FlexyTable
                name={VehInfoDictionary.myDcRentTableProps.tableId}
                tableProps={VehInfoDictionary.myDcRentTableProps}
                records={vehInfoListData.records}
                search={searchText}
                tools={tools}
                onClickTool={onClickToolOnRow}
                onRowDoubleClicked={onDoubleClickRow}
                onChangeCountToDisplay={setCountToDisplay}
            />
            <VehInfoInputRental
                open={editorState.open}
                item={editorState.item}
                onClose={()=>setEditorState(AppObject.EmptyViewerState)}
                responseSaving={editorState.item ? responseEdit : responseAdd}
                onRequestSave={onRequestSave}
            />
        </ContentConainer>
    );
}
